import React from "react";
import { Box, Text, Spinner, Button, VStack, Grid } from "@chakra-ui/react";
import { BillerPlan } from "features/bbps/biller/billerFlowTypes";
import { usePaymentFlowStore } from "hooks/Bbps/paymentFlow/usePaymentFlowStore";

interface PlanListProps {
  plans: BillerPlan[];
  loading: boolean;
  errorMessage?: string;
  disabled?: boolean;
}

const BillerPlanList: React.FC<PlanListProps> = ({
  plans,
  loading,
  errorMessage,
  disabled,
}) => {
  const { selectedPlan, setPlan, setStep } = usePaymentFlowStore();

  if (loading) {
    return (
      <Box textAlign="center" py={4}>
        <Spinner size="sm" />
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Box fontSize="sm" color="red.500" py={2}>
        {errorMessage}
      </Box>
    );
  }

  if (!plans.length) {
    return (
      <Box fontSize="sm" color="gray.500" py={2}>
        No plans available.
      </Box>
    );
  }

  const getTagValue = (tags: any[], name: string) => {
    // Basic normalized check
    const tag = tags?.find(
      (t) => t.paramName?.toLowerCase() === name.toLowerCase(),
    );
    return tag ? tag.paramValue : "-";
  };

  // Define column widths for alignment
  // Circle | Type | Talktime | Data | Validity | Description | Price
  const gridTemplate = "0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 2fr 1fr";

  return (
    <Box w="100%" h="70vh" overflowY="auto" pr={1}>
      {/* Header Row */}
      <Box px={4} mb={2} display={{ base: "none", md: "block" }}>
        <Grid templateColumns={gridTemplate} gap={4} alignItems="center">
          <Text fontSize="xs" fontWeight="bold" color="gray.500">
            Circle
          </Text>
          <Text fontSize="xs" fontWeight="bold" color="gray.500">
            Plan Type
          </Text>
          <Text fontSize="xs" fontWeight="bold" color="gray.500">
            Talktime
          </Text>
          <Text fontSize="xs" fontWeight="bold" color="gray.500">
            Data
          </Text>
          <Text fontSize="xs" fontWeight="bold" color="gray.500">
            Validity
          </Text>
          <Text fontSize="xs" fontWeight="bold" color="gray.500">
            Description
          </Text>
          <Text
            fontSize="xs"
            fontWeight="bold"
            color="gray.500"
            textAlign="center"
          >
            Price
          </Text>
        </Grid>
      </Box>

      {/* Plan Rows */}
      <VStack gap={2} align="stretch">
        {plans.map((p) => {
          const isActive = selectedPlan?.id === p.id;
          const tags = p.planadditionalinfo?.Tag ?? [];

          return (
            <Box
              key={p.id}
              borderWidth="1px"
              borderRadius="lg"
              px={4}
              py={3}
              bg={isActive ? "blue.50" : "white"}
              borderColor={isActive ? "blue.400" : "gray.200"}
              shadow="sm"
              transition="all 0.2s"
              _hover={{ shadow: "md", borderColor: "blue.300" }}
            >
              <Grid
                templateColumns={{ base: "1fr", md: gridTemplate }}
                gap={{ base: 2, md: 4 }}
                alignItems="center"
              >
                {/* Mobile Labels are handy if we collapsed, but keeping it simple for now */}
                <Text
                  fontSize="sm"
                  color="gray.700"
                  display={{ base: "flex", md: "block" }}
                >
                  <Text
                    as="span"
                    fontWeight="bold"
                    mr={2}
                    display={{ base: "block", md: "none" }}
                  >
                    Circle:
                  </Text>
                  {getTagValue(tags, "Circle")}
                </Text>

                <Text
                  fontSize="sm"
                  color="gray.700"
                  display={{ base: "flex", md: "block" }}
                >
                  <Text
                    as="span"
                    fontWeight="bold"
                    mr={2}
                    display={{ base: "block", md: "none" }}
                  >
                    Type:
                  </Text>
                  {getTagValue(tags, "Plan Type")}
                </Text>

                <Text
                  fontSize="sm"
                  color="gray.700"
                  display={{ base: "flex", md: "block" }}
                >
                  <Text
                    as="span"
                    fontWeight="bold"
                    mr={2}
                    display={{ base: "block", md: "none" }}
                  >
                    Talktime:
                  </Text>
                  {getTagValue(tags, "Talktime")}
                </Text>

                <Text
                  fontSize="sm"
                  color="gray.700"
                  display={{ base: "flex", md: "block" }}
                >
                  <Text
                    as="span"
                    fontWeight="bold"
                    mr={2}
                    display={{ base: "block", md: "none" }}
                  >
                    Data:
                  </Text>
                  {getTagValue(tags, "Data")}
                </Text>

                <Text
                  fontSize="sm"
                  color="gray.700"
                  display={{ base: "flex", md: "block" }}
                >
                  <Text
                    as="span"
                    fontWeight="bold"
                    mr={2}
                    display={{ base: "block", md: "none" }}
                  >
                    Validity:
                  </Text>
                  {getTagValue(tags, "Validity")}
                </Text>

                <Text
                  fontSize="sm"
                  color="gray.600"
                  lineClamp={2}
                  title={p.plandescription}
                >
                  {p.plandescription}
                </Text>

                <Box textAlign={{ base: "right", md: "right" }}>
                  <Button
                    size="sm"
                    colorPalette="blue"
                    variant={isActive ? "solid" : "outline"}
                    borderRadius="full"
                    disabled={disabled}
                    onClick={() => {
                      setPlan(p);
                      setStep("SELECT_PLAN");
                    }}
                    _hover={{
                      transform: "translateY(-1px)",
                      shadow: "sm",
                    }}
                    px={4}
                  >
                    ₹{p.amountinrupees}
                  </Button>
                </Box>
              </Grid>
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
};

export default BillerPlanList;
